<script setup lang="ts">
import { ref } from 'vue'
import { t } from '@/i18n'

defineProps<{
  isLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'file-select', file: File): void
}>()

const isDragging = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = true
}

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = false
}

const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = false
  
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    emit('file-select', files[0])
  }
}

const handleFileInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files
  if (files && files.length > 0) {
    emit('file-select', files[0])
  }
}

const openFilePicker = () => {
  fileInput.value?.click()
}
</script>

<template>
  <div 
    class="upload-container card"
    :class="{ 'is-dragging': isDragging, 'is-loading': isLoading }"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
    @click="openFilePicker"
  >
    <input
      ref="fileInput"
      type="file"
      accept="audio/*,.mp3,.wav,.ogg,.aac,.flac,.m4a"
      class="file-input"
      @change="handleFileInput"
      :disabled="isLoading"
    />
    
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <span>{{ t.loading }}</span>
    </div>
    
    <div v-else class="upload-content">
      <p class="upload-title">{{ isDragging ? t.dragDropText : t.uploadPrompt }}</p>
      
      <button class="btn btn-primary btn-lg" @click.stop="openFilePicker">
        <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
          <path d="M11 6.64a1 1 0 0 0-1.243-.97l-1 .25A1 1 0 0 0 8 6.89v4.306A2.6 2.6 0 0 0 7 11c-.5 0-.974.134-1.338.377-.36.24-.662.628-.662 1.123s.301.883.662 1.123c.364.243.839.377 1.338.377s.974-.134 1.338-.377c.36-.24.662-.628.662-1.123V8.89l2-.5z"/>
          <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
        </svg>
        {{ t.uploadButton }}
      </button>
      
      <p class="supported-formats">{{ t.supportedFormats }}</p>
    </div>
  </div>
</template>

<style scoped>
.upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 339px;
  padding: 2rem;
  border: 3px dashed var(--border-color);
  border-radius: var(--radius-xl);
  cursor: pointer;
  transition: border-color var(--transition-normal), background-color var(--transition-normal), transform var(--transition-normal);
  text-align: center;
}

.upload-container:hover {
  border-color: var(--primary-color);
  background: var(--hover-tint);
}

.upload-container.is-dragging {
  border-color: var(--primary-color);
  background: var(--hover-tint-strong);
  transform: scale(1.02);
}

.upload-container.is-loading {
  pointer-events: none;
}

.file-input {
  display: none;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--text-secondary);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-title {
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
}

.supported-formats {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0;
  direction: inherit;
}

@media (max-width: 640px) {
  .upload-container {
    min-height: 250px;
    padding: 1.25rem 0.75rem;
  }

  .upload-title {
    font-size: 1rem;
    padding: 0 0.5rem;
  }

  .supported-formats {
    font-size: 0.75rem;
    padding: 0 0.5rem;
  }
}

@media (max-width: 320px) {
  .upload-container {
    min-height: 220px;
    padding: 1rem 0.5rem;
  }
}
</style>
